import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { TrendChart, BarChartComponent, PieChartComponent } from '../../components/dashboard/Charts';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { calculatePercentage, formatDateRange } from '../../lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    Mail, Send, Inbox, Eye, MousePointer,
    MessageSquare, ThumbsUp, TrendingUp, Award, Calendar
} from 'lucide-react';

export default function EmailPage() {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [hasData, setHasData] = useState(false);

    useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            const response = await reportsApi.getAll();
            setReports(response.data);
            if (response.data.length > 0) {
                setSelectedReportId(response.data[0].id);
                setHasData(true);
            }
        } catch (error) {
            toast.error('Failed to load email data');
        } finally {
            setLoading(false);
        }
    };

    const selectedReport = reports.find(r => r.id === selectedReportId);
    const data = selectedReport?.email || {};

    const trendData = reports.slice(0, 8).reverse().map((report, index) => ({
        name: `W${index + 1}`,
        sent: report.email?.emails_sent || 0,
        opened: report.email?.opened || 0,
        clicked: report.email?.clicked || 0,
    }));

    const industryData = Object.entries(data?.industry_heatmap || {}).map(([name, value]) => ({ name, value }));
    const geoData = Object.entries(data?.geography_heatmap || {}).map(([name, value]) => ({ name, opens: value }));

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-channel-email/30 border-t-channel-email rounded-full animate-spin" />
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="min-h-screen" data-testid="email-page">
                <Header title="Email Performance" subtitle="Outbound email metrics and engagement analytics" />
                <div className="p-8">
                    <div className="card-base p-12 text-center">
                        <Mail className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-xl font-heading font-bold text-white mb-2">No Email Data Available</h3>
                        <p className="text-content-secondary max-w-md mx-auto">Email metrics will appear here once your admin team adds report data.</p>
                    </div>
                </div>
            </div>
        );
    }

    const email = data;
    const deliveryRate = calculatePercentage(email.delivered, email.emails_sent);
    const openRate = calculatePercentage(email.opened, email.delivered);
    const clickRate = calculatePercentage(email.clicked, email.opened);
    const replyRate = calculatePercentage(email.replies, email.delivered);

    return (
        <div className="min-h-screen" data-testid="email-page">
            <Header title="Email Performance" subtitle="Outbound email metrics and engagement analytics" />

            <div className="p-8 space-y-8">
                {/* Week Selector */}
                <div className="flex items-center gap-4" data-testid="email-week-selector">
                    <Calendar className="w-5 h-5 text-content-muted" />
                    <Select value={selectedReportId} onValueChange={setSelectedReportId}>
                        <SelectTrigger className="input-base w-80">
                            <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-card border-line-default max-h-60">
                            {reports.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                    {formatDateRange(r.period_start, r.period_end)} ({r.report_type})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* KPI Cards */}
                <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard title="Emails Sent" value={email.emails_sent || 0} icon={Send} color="email" data-testid="email-sent-kpi" />
                    <KPICard title="Delivered" value={email.delivered || 0} icon={Inbox} data-testid="email-delivered-kpi" />
                    <KPICard title="Opened" value={email.opened || 0} icon={Eye} data-testid="email-opened-kpi" />
                    <KPICard title="Clicked" value={email.clicked || 0} icon={MousePointer} data-testid="email-clicked-kpi" />
                </section>
                <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard title="Replies" value={email.replies || 0} icon={MessageSquare} data-testid="email-replies-kpi" />
                    <KPICard title="Positive Replies" value={email.positive_replies || 0} icon={ThumbsUp} color="inbound" data-testid="email-positive-replies-kpi" />
                    <KPICard title="MQLs" value={email.mqls || 0} icon={TrendingUp} color="red" data-testid="email-mqls-kpi" />
                    <KPICard title="Qualified Leads" value={email.qualified_leads || 0} icon={Award} color="inbound" data-testid="email-qualified-leads-kpi" />
                </section>

                {/* Funnel Metrics */}
                <section className="card-base p-6" data-testid="email-funnel-metrics">
                    <h3 className="text-lg font-heading font-bold text-white mb-6">EMAIL FUNNEL METRICS</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { label: 'Delivery Rate', value: deliveryRate, color: '#10B981' },
                            { label: 'Open Rate', value: openRate, color: '#EA4335' },
                            { label: 'Click Rate', value: clickRate, color: '#DC2626' },
                            { label: 'Reply Rate', value: replyRate, color: '#DC2626', scale: 3 },
                        ].map(({ label, value, color, scale }) => (
                            <div key={label} className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-content-secondary">{label}</span>
                                    <span className="text-lg font-bold" style={{ color }}>{value}%</span>
                                </div>
                                <div className="progress-bar">
                                    <div className="progress-bar-fill" style={{ width: `${Math.min(value * (scale || 1), 100)}%`, background: color }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Charts */}
                {trendData.some(d => d.opened > 0 || d.clicked > 0) && (
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TrendChart data={trendData} dataKey="opened" title="OPEN TREND" color="#EA4335" />
                        <TrendChart data={trendData} dataKey="clicked" title="CLICK TREND" color="#FF3B30" />
                    </section>
                )}
                {(industryData.length > 0 || geoData.length > 0) && (
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {industryData.length > 0 && <PieChartComponent data={industryData} title="INDUSTRY HEATMAP" />}
                        {geoData.length > 0 && <BarChartComponent data={geoData} dataKeys={['opens']} title="GEOGRAPHY HEATMAP" />}
                    </section>
                )}
            </div>
        </div>
    );
}
